import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  Container,
  Form,
  FormControl,
  InputGroup,
  Nav,
  Navbar,
  ListGroup,
} from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { HiOutlineShoppingCart } from 'react-icons/hi2';
import { useSearch } from '../../context/SearchContext';
import { useCarrito } from '../../context/CarritoContext';
import { NavLink, useNavigate } from 'react-router-dom';
import { obtenerURLImagen, obtenerCoverImagen } from '../../utils/imageUtils';
import CarritoSidebar from '../cart/CarritoSidebar';
import AnnouncementBar from '../common/AnnouncementBar';
import { ROUTES, BRAND, presupuestoWhatsAppUrl, labelCategoriaApi } from '../../constants';
import { generarTituloProducto } from '../../utils/formatters';
import './MyNavbar.css';

const cotizarCajasUrl = presupuestoWhatsAppUrl(
  'Hola, quiero cotizar cajas a medida.'
);

const SearchForm = ({ className = '', onAfterNavigate = () => {} }) => {
  const { sugerenciasDe, abrirProducto, abrirMejorResultado, catalogoListo } = useSearch();
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [submittedEmpty, setSubmittedEmpty] = useState(false);

  const queryTrim = query.trim();
  const puedeBuscar = queryTrim.length >= 1;
  const sugerencias = useMemo(
    () => (puedeBuscar ? sugerenciasDe(queryTrim) : []),
    [puedeBuscar, queryTrim, sugerenciasDe]
  );

  const abrirYCerrar = useCallback(
    (producto) => {
      if (!producto) return;
      abrirProducto(producto);
      setQuery(generarTituloProducto(producto));
      setShowSuggestions(false);
      setSubmittedEmpty(false);
      onAfterNavigate();
    },
    [abrirProducto, onAfterNavigate]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!queryTrim) return;
      const destacado =
        (sugerencias[highlightedIndex] && sugerencias[highlightedIndex]) ||
        abrirMejorResultado(queryTrim);
      if (destacado) {
        abrirYCerrar(destacado);
        return;
      }
      setSubmittedEmpty(true);
      setShowSuggestions(true);
    },
    [queryTrim, sugerencias, highlightedIndex, abrirMejorResultado, abrirYCerrar]
  );

  const handleSearchChange = useCallback((e) => {
    setQuery(e.target.value);
    setHighlightedIndex(0);
    setSubmittedEmpty(false);
    setShowSuggestions(e.target.value.trim().length >= 1);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestions) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          sugerencias.length === 0 ? 0 : (prev + 1) % sugerencias.length
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          sugerencias.length === 0 ? 0 : (prev - 1 + sugerencias.length) % sugerencias.length
        );
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    },
    [showSuggestions, sugerencias.length]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => setShowSuggestions(false), 180);
  }, []);

  const handleFocus = useCallback(() => {
    if (query.trim().length >= 1) {
      setShowSuggestions(true);
    }
  }, [query]);

  const mostrarPanel = showSuggestions && queryTrim.length >= 1;
  const mensajeVacio = !catalogoListo
    ? 'Cargando catálogo…'
    : 'No encontramos productos para esa búsqueda.';

  return (
    <div className="search-container position-relative">
      <Form className={`d-flex ${className}`} onSubmit={handleSubmit} role="search">
        <InputGroup className="w-100">
          <FormControl
            type="search"
            placeholder="Buscá por medida, modelo o tipo"
            aria-label="Buscar productos"
            aria-autocomplete="list"
            aria-expanded={mostrarPanel}
            value={query}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="btn-close search-clear-btn"
              aria-label="Limpiar búsqueda"
              onClick={() => {
                setQuery('');
                setShowSuggestions(false);
                setSubmittedEmpty(false);
              }}
            />
          )}
          <Button variant="outline-secondary" type="submit" aria-label="Buscar">
            <FaSearch />
          </Button>
        </InputGroup>
      </Form>

      {mostrarPanel && (
        <div className="suggestions-container" role="listbox">
          {sugerencias.length > 0 ? (
            <ListGroup className="suggestions-list">
              {sugerencias.map((producto, index) => (
                <ListGroup.Item
                  key={producto.id}
                  action
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => abrirYCerrar(producto)}
                  className={`suggestion-item${index === highlightedIndex ? ' is-active' : ''}`}
                >
                  <div className="suggestion-content">
                    <img
                      src={obtenerURLImagen(obtenerCoverImagen(producto))}
                      alt=""
                      className="suggestion-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="suggestion-text">
                      <div className="suggestion-title">{generarTituloProducto(producto)}</div>
                      <div className="suggestion-meta">{labelCategoriaApi(producto.categoria)}</div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="suggestion-empty">
              {mensajeVacio}
              {submittedEmpty && catalogoListo ? ' Probá con la medida (ej. 30x20) o el tipo de caja.' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function MyNavbar() {
  const headerRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { cantidadTotal, showCarritoSidebar, setShowCarritoSidebar } = useCarrito();
  const navigate = useNavigate();

  const toggleMenu = useCallback(() => {
    setShowMenu(prev => !prev);
  }, []);
  const closeMenu = useCallback(() => setShowMenu(false), []);
  const toggleCarrito = useCallback(() => setShowCarritoSidebar(prev => !prev), [setShowCarritoSidebar]);
  const closeCarrito = useCallback(() => setShowCarritoSidebar(false), [setShowCarritoSidebar]);

  const cartAriaLabel = cantidadTotal > 0
    ? `Abrir carrito, ${cantidadTotal} ${cantidadTotal === 1 ? 'producto' : 'productos'}`
    : 'Abrir carrito, vacío';

  const renderCartButton = (extraClass) => (
    <button
      type="button"
      className={`nav-cart-btn ${extraClass}${cantidadTotal > 0 ? ' has-items' : ''}`}
      onClick={toggleCarrito}
      aria-label={cartAriaLabel}
    >
      <HiOutlineShoppingCart aria-hidden="true" focusable="false" />
      {cantidadTotal > 0 && (
        <span className="cart-badge" aria-hidden="true">
          {cantidadTotal > 99 ? '99+' : cantidadTotal}
        </span>
      )}
    </button>
  );

  const scrollToHash = useCallback((hash) => {
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleHashClick = useCallback((hash) => {
    if (window.location.pathname === '/') {
      scrollToHash(hash);
    } else {
      navigate(`/#${hash}`);
    }
    closeMenu();
  }, [navigate, scrollToHash, closeMenu]);

  const handleNavClick = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    closeMenu();
  }, [closeMenu]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;

    const syncOffset = () => {
      const heightPx = Math.ceil(header.getBoundingClientRect().height);
      if (!heightPx) return;
      const height = `${heightPx}px`;
      const root = document.documentElement;
      root.style.setProperty('--header-offset', height);
      root.style.setProperty('--header-offset-mobile', height);
    };

    syncOffset();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncOffset)
      : null;
    observer?.observe(header);

    const onFonts = () => syncOffset();
    document.fonts?.ready?.then(onFonts);
    window.addEventListener('load', syncOffset);
    window.addEventListener('resize', syncOffset);

    return () => {
      observer?.disconnect();
      window.removeEventListener('load', syncOffset);
      window.removeEventListener('resize', syncOffset);
    };
  }, []);

  const navItems = [
    { key: 'inicio', label: 'Inicio', type: 'route', path: ROUTES.HOME },
    { key: 'productos', label: 'Productos', type: 'route', path: ROUTES.CAJAS },
    { key: 'contacto', label: 'Contacto', type: 'route', path: ROUTES.CONTACTO },
  ];

  const renderNavItem = (item, className = '') => {
    if (item.type === 'hash') {
      return (
        <Nav.Link
          key={item.key}
          as="button"
          type="button"
          className={className}
          onClick={() => handleHashClick(item.hash)}
        >
          {item.label}
        </Nav.Link>
      );
    }
    if (item.type === 'external') {
      return (
        <Nav.Link
          key={item.key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={closeMenu}
        >
          {item.label}
        </Nav.Link>
      );
    }
    return (
      <Nav.Link
        key={item.key}
        as={NavLink}
        to={item.path}
        onClick={handleNavClick}
        className={className}
      >
        {item.label}
      </Nav.Link>
    );
  };

  return (
    <div
      ref={headerRef}
      className={`site-header ${isLoaded ? 'navbar-loaded' : ''} ${scrolled ? 'is-scrolled' : ''}`}
    >
      <span className="visually-hidden" aria-live="polite" aria-atomic="true">
        {cantidadTotal > 0
          ? `${cantidadTotal} ${cantidadTotal === 1 ? 'producto' : 'productos'} en el carrito`
          : 'Carrito vacío'}
      </span>
      <AnnouncementBar />
      <Navbar expand="md" className="bg-body-tertiary shadow-sm site-navbar">
      <Container fluid className="nav-style">
        {/* Desktop */}
        <div className="d-none d-md-flex desktop-top-bar">
          <div className="desktop-top-left">
            <Navbar.Brand as={NavLink} to="/" className="desktop-logo">
              <img src="/logo.png" alt={BRAND.NAME} className="navbar-logo" />
            </Navbar.Brand>
            <div className="desktop-search-container">
              <SearchForm className="buscador-wrapper" />
            </div>
          </div>
          <div className="desktop-top-right">
            <a
              href={cotizarCajasUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="desktop-cotizar-btn"
            >
              Cotizar cajas a medida
            </a>
            {renderCartButton('desktop-cart-icon')}
          </div>
        </div>

        <hr className="separador-nav d-none d-md-block w-100" />

        <Nav className="justify-content-center d-none d-md-flex navlink-text">
          {navItems.map((item) => renderNavItem(item))}
        </Nav>

        {/* Mobile */}
        <div className="d-md-none w-100 d-flex justify-content-between align-items-center mobile-bar">
          <Button
            variant="outline-secondary"
            className={`menu-btn ${showMenu ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Abrir menú"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <span className="navbar-toggler-icon" />
          </Button>
          <Navbar.Brand as={NavLink} to="/" className="m-0">
            <img src="/logo.png" alt={BRAND.NAME} className="navbar-logo-mobile" />
          </Navbar.Brand>
          {renderCartButton('mobile-cart-icon')}
        </div>

        {/* Mobile Menu */}
        {showMenu && (
          <div
            className="mobile-menu-overlay d-md-none"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 9998,
            }}
            onClick={closeMenu}
          >
            <div
              className="mobile-menu-content"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '80%',
                height: '100vh',
                backgroundColor: 'white',
                padding: '1rem',
                zIndex: 9999,
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <img src="/logo.png" alt={BRAND.NAME} className="navbar-logo-mobile-menu" />
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeMenu}
                  aria-label="Cerrar"
                />
              </div>
              <SearchForm className="mb-3" onAfterNavigate={closeMenu} />
              <Nav className="flex-column text-start">
                {navItems.map((item) => renderNavItem(item, 'py-2'))}
              </Nav>
              <a
                href={cotizarCajasUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-menu-cotizar"
                onClick={closeMenu}
              >
                Cotizar cajas a medida
              </a>
            </div>
          </div>
        )}
      </Container>

      <CarritoSidebar show={showCarritoSidebar} handleClose={closeCarrito} />
    </Navbar>
    </div>
  );
}

export default MyNavbar;
